
import React, { useState } from "react";
import {
  Users,
  UserRoundCheck,
  UserPlus,
  IndianRupee,
  TrendingUp,
  TrendingDown,
  Crown,
  Star, 
  CalendarDays,
} from "lucide-react";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

const statistics = [
  {
    title: "Total Members",
    value: "1,250",
    change: "12.5%",
    description: "vs last month",
    icon: Users,
    color: "blue",
  },
  {
    title: "Subscription Members",
    value: "835",
    change: "8.7%",
    description: "66.8% of total members",
    icon: UserRoundCheck,
    color: "pink",
  },
    {
    title: "New Registrations",
    value: "86",
    change: "15.2%",
    description: "This month",
    icon: UserPlus,
    color: "orange",
  },
  {
    title: "Total Revenue",
    value: "₹4.82L",
    change: "18.4%",
    description: "This month",
    icon: IndianRupee,
    color: "green",
  }
];

const registrationData = [
  { month: "May", registrations: 72 },
  { month: "Jun", registrations: 95 },
  { month: "Jul", registrations: 110 },
  { month: "Aug", registrations: 126 },
  { month: "Sep", registrations: 142 },
  { month: "Oct", registrations: 86 },
];

const subscriptionPlans = [
  { name: "Basic", count: 287, width: "48%", color: "blue", icon: Star },
  { name: "Premium", count: 394, width: "68%", color: "pink", icon: Star },
  { name: "Gold", count: 154, width: "31%", color: "gold", icon: Crown },
];

function PageHeading() {
  return (
    <div className="mb-6 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-800">
          Dashboard
        </h1>
        <p className="mt-2 text-xs leading-5 text-slate-500">
          Welcome back! Here's what's happening with Salem Kongu Matrimony.
        </p>
      </div>

      <div className="flex items-center gap-2 self-start rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-[10px] font-medium text-slate-600 sm:self-auto">
        <CalendarDays size={15} className="text-blue-600" />
        14 October 2026
      </div>
    </div>
  );
}

function StatisticCard({ item }) {
  const Icon = item.icon;
  const TrendIcon = item.negative ? TrendingDown : TrendingUp;

  return (
    <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm shadow-slate-200/20">
      <div className="flex items-center justify-between gap-2">
        <div className={`stat-icon ${item.color}`}>
          <Icon size={19} />
        </div>

        <span
          className={`flex items-center gap-1 whitespace-nowrap text-[9px] font-semibold ${
            item.negative ? "text-rose-500" : "text-emerald-600"
          }`}
        >
          <TrendIcon size={12} />
          {item.change}
        </span>
      </div>

      <h2 className="mt-4 truncate text-2xl font-bold tracking-tight text-slate-800">
        {item.value}
      </h2>

      <p className="mt-1 truncate text-[10px] font-medium text-slate-600">
        {item.title}
      </p>

      <p className="mt-2 truncate text-[9px] text-slate-400">
        {item.description}
      </p>
    </div>
  );
}

function PanelHeading({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <h3 className="text-xs font-bold text-slate-800">{title}</h3>
        <p className="mt-1.5 text-[10px] text-slate-400">{subtitle}</p>
      </div>
      {action}
    </div>
  );
}

function RegistrationChart() {
  const [period, setPeriod] = useState("6");

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
      <PanelHeading
        title="Member Registration Overview"
        subtitle="Monthly new member registrations"
        action={
          <select
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
            className="max-w-[125px] rounded-lg border border-slate-200 bg-white px-2 py-2 text-[9px] text-slate-600 outline-none focus:border-blue-400"
          >
            <option value="6">Last 6 Months</option>
            <option value="12">Last 12 Months</option>
          </select>
        }
      />

      <div className="mt-5 h-[220px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={registrationData}
            margin={{ top: 18, right: 5, left: -23, bottom: 0 }}
          >
            <CartesianGrid
              vertical={false}
              stroke="#edf0f6"
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#8491a8" }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 9, fill: "#8491a8" }}
            />
            <Tooltip
              cursor={{ fill: "#f5f7fc" }}
              contentStyle={{
                borderRadius: 8,
                border: "1px solid #e6eaf2",
                fontSize: 11,
              }}
            />
            <Bar
              dataKey="registrations"
              name="Registrations"
              fill="#2864e8"
              radius={[5, 5, 0, 0]}
              maxBarSize={35}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 border-t border-slate-100 pt-4">
        <span className="flex items-center gap-2 text-[10px] text-slate-400">
          <span className="h-2 w-2 rounded-full bg-blue-600" />
          New Registrations
        </span>
        <span className="text-xs font-bold text-slate-700">
          631 registrations
        </span>
      </div>
    </section>
  );
}

function SubscriptionOverview() {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
      <PanelHeading
        title="Subscription Overview"
        subtitle="Current active subscriptions"
        action={<Crown size={19} className="text-blue-600" />}
      />

      <div className="mt-7 space-y-5">
        {subscriptionPlans.map((plan) => {
          const Icon = plan.icon;

          return (
            <div key={plan.name} className="flex items-center gap-3">
              <div className={`plan-icon ${plan.color}`}>
                <Icon size={16} />
              </div>

              <span className="w-[55px] text-[10px] font-semibold text-slate-700">
                {plan.name}
              </span>

              <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full progress-${plan.color}`}
                  style={{ width: plan.width }}
                />
              </div>

              <span className="min-w-[36px] rounded-md bg-blue-50 px-2 py-1.5 text-center text-[10px] font-semibold text-blue-600">
                {plan.count}
              </span>
            </div>
          );
        })}
      </div>

      <div className="mt-7 flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-4">
        <span className="text-[10px] text-slate-500">
          Total Active Subscriptions
        </span>
        <span className="text-sm font-bold text-slate-800">835</span>
      </div>
    </section>
  );
}

export default function Dashboard() {
  return (
    <div className="mx-auto max-w-[1800px]">
      <PageHeading />

      <section className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {statistics.map((item) => (
          <StatisticCard key={item.title} item={item} />
        ))}
      </section>

      <section className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <RegistrationChart />
        <SubscriptionOverview />
      </section>

      <footer className="mt-6 flex flex-wrap justify-between gap-2 border-t border-slate-200 pt-4 text-[9px] text-slate-400">
        <span>© {new Date().getFullYear()} Salem Kongu Matrimony</span>
        <span>Admin Dashboard</span>
      </footer>
    </div>
  );
}
